import React, { ChangeEvent } from "react";
import { BaseEditorComponent } from "@handsontable/react";
import { Box, Dialog, TextField } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CustomButton from "../../../../../../components/Button/CustomButton";
import { tesaGrey1 } from "../../../../../../styles/colors";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const NativeListener = require("react-native-listener").default;

interface NoteCustomValue {
  repetitionNumber: number;
  testResultRowId: number;
  text: string;
}

interface NoteEditorComponentState {
  description: string;
  value: string;
  visible: boolean;
}

interface NoteEditorComponentProps {
  testCaseNumber: number;
  readOnly: boolean;
  editorColumnScope: number;
  "hot-editor": any;
}

const onFocus = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  const lengthOfInput = event.target.value.length;
  return event.target.setSelectionRange(lengthOfInput, lengthOfInput);
};

export class NoteEditorComponent extends BaseEditorComponent<NoteEditorComponentProps, NoteEditorComponentState> {
  private mainElementRef: HTMLInputElement | null;

  private mounted = false;

  constructor(props: NoteEditorComponentProps) {
    super(props);
    this.mainElementRef = null;
    this.state = {
      value: "",
      visible: false,
    } as NoteEditorComponentState;
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  open() {
    this.setState((prev) => {
      return { ...prev, visible: true };
    });
  }

  close() {
    this.setState((prev) => {
      return { ...prev, visible: false };
    });
  }

  setValue(value: string, callback?: () => void): void {
    const parsedValue = value
      ? (JSON.parse(value) as NoteCustomValue)
      : { repetitionNumber: 0, testResultRowId: 0, text: "" };
    this.setState((prev) => {
      return { ...prev, value, description: parsedValue.text };
    }, callback);
  }

  getValue(): string {
    const { value } = this.state as NoteEditorComponentState;
    return value;
  }

  private onChangeDescription = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    this.setState({ description: event.target.value });
  };

  private inputRef = (ref: HTMLInputElement) => {
    this.mainElementRef = ref;
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" || event.key === "Esc") {
      this.close();
    } else if (event.key === "Enter" && !event.shiftKey) {
      this.storeValue();
    } else {
      event.stopPropagation();
    }
  };

  private onKeyDownInTextField = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!event.shiftKey && event.key === "Enter") {
      event.preventDefault();
      this.closeAction();
    }
  };

  private saveAction = (): void => {
    this.storeValue(() => this.finishEditing());
  };

  private closeAction = (): void => {
    this.finishEditing(true);
  };

  private storeValue(callback?: () => void) {
    const { description } = this.state as NoteEditorComponentState;
    const { repetitionNumber, testResultRowId } = this.value;
    this.setValue(JSON.stringify({ repetitionNumber, testResultRowId, text: description }), callback);
  }

  get value(): NoteCustomValue {
    const { value } = this.state as NoteEditorComponentState;
    return value ? (JSON.parse(value) as NoteCustomValue) : { repetitionNumber: 0, testResultRowId: 0, text: "" };
  }

  render(): React.ReactElement | undefined {
    const { testCaseNumber, readOnly } = this.props as NoteEditorComponentProps;
    const { visible, description } = this.state as NoteEditorComponentState;
    const { repetitionNumber } = this.value;
    return (
      <Dialog fullWidth maxWidth="sm" open={visible} onClose={() => {}}>
        <NativeListener
          onMouseDown={(e: MouseEvent) => {
            e.stopPropagation();
          }}
          onKeyDown={this.onKeyDown}
        >
          <>
            <Box padding={4}>
              <Box display="flex" justifyContent="flex-end">
                <CloseIcon
                  style={{
                    cursor: "pointer",
                    marginRight: -5,
                    color: tesaGrey1,
                  }}
                  onClick={this.closeAction}
                />
              </Box>
              <h2>{`NOTE TO REPETITION ${repetitionNumber} of TEST ID ${testCaseNumber}`}</h2>
              <TextField
                InputProps={{
                  style: { height: 95, paddingTop: 32 },
                }}
                disabled={readOnly}
                fullWidth
                autoFocus
                multiline
                rows={2}
                label="Note"
                value={description}
                onChange={this.onChangeDescription}
                onFocus={onFocus}
                onKeyDown={this.onKeyDownInTextField}
                variant="outlined"
              />
              <Box display="flex" justifyContent="flex-end" marginTop={3}>
                <CustomButton primary text={readOnly ? "Close" : "Save"} onClick={this.saveAction} />
              </Box>
            </Box>
          </>
        </NativeListener>
      </Dialog>
    );
  }
}
export default NoteEditorComponent;
